import { Box, Chip, Divider, Fade, Typography, useTheme } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog, useMovieFinder } from '@/logic/state';

import ClearIcon from '@mui/icons-material/Clear';
import { DateTime } from '@/utility/luxon';
import { FinderFiltersResponse } from '@/logic/services/movie-finder';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';

enum FilterType {
  Experience = 'Experience',
  Movie = 'MOVIE',
  Cinema = 'CINEMA',
}
export interface Filter {
  id: number;
  type: FilterType;
  label: string;
}

interface FiltersProps {
  finderFiltersResults: FinderFiltersResponse;
}

export const Filters: FC<FiltersProps> = ({ finderFiltersResults }) => {
  const theme = useTheme();
  const { locale } = useRouter();
  const [selectedFilters, setSelectedFiltersLocal] = useState<Filter[]>([]);
  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);
  const intl = useIntl();
  const [
    selectedDate,
    selectedTimeDuration,
    selectedCinemas,
    setSelectedCinemas,
    selectedExperiences,
    setSelectedExperiences,
    selectedMovies,
    setSelectedMovies,
    setSelectedFilters,
  ] = useMovieFinder(
    s => [
      s.selectedDate,
      s.selectedTimeDuration,
      s.selectedCinemas,
      s.setSelectedCinemas,
      s.selectedExperiences,
      s.setSelectedExperiences,
      s.selectedMovies,
      s.setSelectedMovies,
      s.setSelectedFilters,
    ],
    shallow
  );

  const buildNewFiltersList = (): void => {
    const filtersList: Filter[] = [];
    selectedCinemas.forEach(id => {
      filtersList.push({
        id,
        type: FilterType.Cinema,
        label: finderFiltersResults?.cinemas?.find(cinema => cinema.id === id)?.name,
      });
    });
    selectedExperiences.forEach(id => {
      filtersList.push({
        id,
        type: FilterType.Experience,
        label: finderFiltersResults?.experiences?.find(experience => experience.id === id)
          ?.name,
      });
    });
    selectedMovies.forEach(id => {
      filtersList.push({
        id,
        type: FilterType.Movie,
        label: finderFiltersResults?.films?.find(film => film.id === id)?.title,
      });
    });
    setSelectedFiltersLocal(filtersList);
  };

  useEffect(() => {
    setSelectedFilters(selectedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  useEffect(() => {
    !!finderFiltersResults && buildNewFiltersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCinemas, selectedExperiences, selectedMovies, finderFiltersResults]);

  const getFiltersLabel = (): string => {
    if (!selectedDate) return '';
    const isToday =
      DateTime.fromISO(selectedDate) <= DateTime.fromISO(DateTime.now().toISO());
    const isTomorrow =
      DateTime.fromISO(selectedDate) > DateTime.fromISO(DateTime.now().toISO()) &&
      DateTime.fromISO(selectedDate) <=
        DateTime.fromISO(DateTime.now().plus({ days: 1 }).toISO());
    const label: string = intl.formatMessage({ id: 'global.showing' });
    const dayLabel: string =
      !isToday && !isTomorrow
        ? DateTime.fromISO(selectedDate, {
            locale: locale,
          }).weekdayShort
        : intl.formatMessage({ id: `global.${isToday ? 'today' : 'tomorrow'}` });
    const dayDate = DateTime.fromISO(selectedDate, {
      locale: locale,
    }).toFormat('dd');
    const monthDate = DateTime.fromISO(selectedDate, {
      locale: locale,
    }).monthShort;
    const timeDuration: string = intl.formatMessage({
      id: `movie.finder.showtimes.${selectedTimeDuration.toLowerCase()}`,
    });
    return `(${label} ${dayLabel} ${!isToday && !isTomorrow ? dayDate : ''} ${
      !isToday && !isTomorrow ? ` - ${monthDate}` : ''
    }, ${timeDuration})`;
  };

  const handleDelete = ({ id, type }: Partial<Filter>): void => {
    if (type === FilterType.Cinema) {
      setSelectedCinemas(selectedCinemas.filter(_id => _id !== id));
    }
    if (type === FilterType.Experience) {
      setSelectedExperiences(selectedExperiences.filter(_id => _id !== id));
    }
    if (type === FilterType.Movie) {
      setSelectedMovies(selectedMovies.filter(_id => _id !== id));
    }
  };

  const checkIfAllowedToClearAllFilters = (): boolean => {
    if (
      selectedMovies.length !== 0 ||
      selectedCinemas.length !== 0 ||
      selectedExperiences.length !== 0
    ) {
      setDialog({
        wrapper: true,
        isAllowedToClearAllFiltersDialog: true,
      });
      return false;
    }
    return true;
  };

  const clearFilters = (): void => {
    setSelectedCinemas([]);
    setSelectedExperiences([]);
    setSelectedMovies([]);
  };

  useEffect(() => {
    if (!!!dialog?.isAllowedToClearAllFiltersDialogPayload) return;
    const { isAllowed } = dialog?.isAllowedToClearAllFiltersDialogPayload;
    if (isAllowed) {
      clearFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog]);

  const FilterChip: FC<Filter> = ({ id, label, type }): JSX.Element => {
    const getChipLabel = (label: string): JSX.Element => {
      return (
        <Typography
          sx={{
            fontSize: pxToRem(16),
            fontWeight: 400,
            lineHeight: '24px',
          }}
        >
          {label}
        </Typography>
      );
    };

    return (
      <Chip
        label={getChipLabel(label)}
        sx={{
          marginBlock: {
            mobile: '12px',
            tablet: pxToRem(16),
          },
          marginRight: '12px',
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primary.main,
          cursor: 'pointer',
        }}
        onDelete={() => handleDelete({ id, type, label })}
        deleteIcon={<ClearIcon />}
      />
    );
  };

  return (
    <Box
      sx={{
        marginTop: pxToRem(24),
      }}
    >
      <Typography
        sx={{
          fontSize: {
            mobile: '16px',
            tablet: pxToRem(16),
          },
          fontFamily: theme.palette.typography.font.bold,
          color: theme.palette.grey[90],
          fontWeight: 700,
          marginBottom: pxToRem(16),
        }}
      >
        <FormattedMessage id='movie.finder.filters.your_filters' />
        {getFiltersLabel()}
      </Typography>

      <Box>
        {selectedFilters.length ? (
          <>
            <Chip
              label={<FormattedMessage id='movie.finder.filters.clear_all' />}
              sx={{
                marginBlock: {
                  mobile: '12px',
                  tablet: pxToRem(16),
                },
                marginRight: '12px',
                backgroundColor: theme.palette.lightGrey,
                cursor: 'pointer',
              }}
              onClick={() => checkIfAllowedToClearAllFilters()}
            />
            {selectedFilters.map(({ id, label, type }, index: number) => (
              <Fade key={index} in={true} appear unmountOnExit>
                <Box component={'span'}>
                  <FilterChip key={index} id={id} label={label} type={type} />
                </Box>
              </Fade>
            ))}
          </>
        ) : (
          <Typography
            sx={{
              fontSize: {
                mobile: '16px',
                tablet: pxToRem(16),
              },
              fontFamily: theme.palette.typography.font.regular,
              color: theme.palette.grey[50],
              fontWeight: 400,
              lineHeight: '24px',
            }}
          >
            <FormattedMessage id='movie.finder.filters.no_selected_filters' />
          </Typography>
        )}
      </Box>

      <Divider />
    </Box>
  );
};
